import axiosInstance from '../../../lib/axios'
import { openEventStream, type StreamErrorInfo } from '../../../lib/sse'
import { API_ENDPOINTS } from '../../../constants/api'
import type {
  Transcript,
  TranscriptStreamEvent,
  TranscriptPreviewEvent,
  TranscriptInterimEvent,
} from '../../../types/transcript.types'

export type TranscriptStreamErrorInfo = StreamErrorInfo

export interface TranscriptStreamHandlers {
  onTranscript: (payload: TranscriptStreamEvent) => void
  onPreview: (payload: TranscriptPreviewEvent) => void
  onInterim?: (payload: TranscriptInterimEvent) => void
  onOpen?: () => void
  onError?: (info: TranscriptStreamErrorInfo) => void
  onAuthRevoked?: () => void
}

export const transcriptsService = {
  async getAll(day?: string, userId?: number, propertyId?: number): Promise<Transcript[]> {
    const params = new URLSearchParams()
    if (day) params.set('day', day)
    if (userId !== undefined) params.set('user_id', String(userId))
    if (propertyId !== undefined) params.set('property_id', String(propertyId))

    const queryString = params.toString()
    const endpoint = queryString
      ? API_ENDPOINTS.TRANSCRIPTS_FILTERED(queryString)
      : API_ENDPOINTS.TRANSCRIPTS

    const { data } = await axiosInstance.get<Transcript[]>(endpoint)
    return data
  },

  // The reconnect/backoff machinery lives in lib/sse — the restaurant stream is
  // wire-identical, and the backend shares its framing helpers for the same
  // reason: two copies would drift.
  streamLive(propertyId: number, handlers: TranscriptStreamHandlers): () => void {
    return openEventStream(API_ENDPOINTS.TRANSCRIPTS_STREAM(propertyId), {
      events: {
        transcript: (data) => handlers.onTranscript(data as TranscriptStreamEvent),
        transcript_preview: (data) => handlers.onPreview(data as TranscriptPreviewEvent),
        transcript_interim: (data) => handlers.onInterim?.(data as TranscriptInterimEvent),
      },
      onOpen: handlers.onOpen,
      onError: handlers.onError,
      onAuthRevoked: handlers.onAuthRevoked,
    })
  },
}
